import React, { useEffect, useState } from "react";
import {
  Plus, Trash2, ArrowUp, ArrowDown, Pencil, Copy, Eye, ClipboardList, ArrowLeft,
  Check, X, RotateCcw, Download, Clock, FileText, Loader2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import StatusBadge from "../components/StatusBadge";
import FormField from "../components/FormField";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

const FIELD_TYPES = ["text", "paragraph", "email", "phone", "address", "date", "number", "currency", "dropdown", "radio", "checkboxes", "yesno", "file", "signature", "initials", "instructions", "richtext", "section", "divider"];
const HAS_OPTIONS = new Set(["dropdown", "radio", "checkboxes"]);
const HAS_CONTENT = new Set(["instructions", "richtext"]);
const DISPLAY = new Set(["instructions", "richtext", "section", "divider"]);
const STAGES = ["forms", "onboarding", "background", "client"];
const uid = () => "f" + Math.random().toString(36).slice(2, 9);
const EMPTY = { title: "", description: "", stage: "forms", fields: [], required: true, published: false };

// ---------------- Builder ----------------
function FormBuilder({ formId, onBack }) {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [id, setId] = useState(formId === "new" ? null : formId);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (formId && formId !== "new") api.get(`/admin/forms/${formId}`).then((r) => setForm({ ...EMPTY, ...r.data })).catch(() => {});
  }, [formId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addField = (type) => set("fields", [...form.fields, { id: uid(), type, label: type === "divider" ? "" : "Untitled", required: false, options: HAS_OPTIONS.has(type) ? ["Option 1"] : undefined, content: HAS_CONTENT.has(type) ? "" : undefined }]);
  const updField = (i, patch) => set("fields", form.fields.map((f, idx) => idx === i ? { ...f, ...patch } : f));
  const rmField = (i) => set("fields", form.fields.filter((_, idx) => idx !== i));
  const moveField = (i, d) => { const n = [...form.fields]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; set("fields", n); };

  const persist = async (published) => {
    if (!form.title.trim()) { toast({ title: "Title required", variant: "destructive" }); return; }
    const payload = { ...form, published };
    setSaving(true);
    try {
      let res;
      if (id) res = await api.put(`/admin/forms/${id}`, payload);
      else { res = await api.post("/admin/forms", payload); setId(res.data.id); }
      setForm({ ...EMPTY, ...res.data });
      toast({ title: published ? "Form published" : "Draft saved" });
    } catch (e) { toast({ title: "Save failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="form-builder">
      <div className="flex items-center justify-between">
        <button onClick={onBack} data-testid="builder-back-btn" className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-violet-600"><ArrowLeft className="h-4 w-4" /> Back to forms</button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setPreview(true)} data-testid="preview-btn" className="gap-1.5"><Eye className="h-4 w-4" /> Preview</Button>
          <Button variant="outline" onClick={() => persist(false)} disabled={saving} data-testid="form-draft-btn" className="gap-1.5">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save Draft</Button>
          <Button onClick={() => persist(true)} disabled={saving} data-testid="form-publish-btn" className="btn-primary-gradient gap-1.5 border-0 text-white">{form.published ? "Update (Published)" : "Publish"}</Button>
        </div>
      </div>

      <Card>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5 sm:col-span-2"><Label>Form title</Label><Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="form-title-input" /></div>
          <div className="space-y-1.5 sm:col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="form-desc-input" /></div>
          <div className="space-y-1.5"><Label>Onboarding stage</Label>
            <Select value={form.stage} onValueChange={(v) => set("stage", v)}><SelectTrigger data-testid="form-stage-select"><SelectValue /></SelectTrigger><SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
          </div>
          <label className="flex items-center gap-2 pt-6 text-sm text-slate-600"><Switch checked={form.required} onCheckedChange={(v) => set("required", v)} data-testid="form-required-switch" /> Required form</label>
        </div>
      </Card>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-900">Fields</h3>
          <Select value="" onValueChange={addField}>
            <SelectTrigger className="w-56" data-testid="add-field-select"><SelectValue placeholder="+ Add field…" /></SelectTrigger>
            <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          {form.fields.map((f, i) => (
            <div key={f.id} className="rounded-xl border border-slate-100 p-3" data-testid={`builder-field-${i}`}>
              <div className="flex items-center justify-between">
                <span className="rounded bg-violet-50 px-2 py-0.5 text-xs font-semibold uppercase text-violet-600">{f.type}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveField(i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-violet-600 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                  <button onClick={() => moveField(i, 1)} disabled={i === form.fields.length - 1} className="p-1 text-slate-400 hover:text-violet-600 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                  <button onClick={() => rmField(i)} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {f.type !== "divider" && (
                <Input className="mt-2" value={f.label} onChange={(e) => updField(i, { label: e.target.value })} data-testid={`field-label-${i}`} placeholder="Field label / heading" />
              )}
              {HAS_CONTENT.has(f.type) && (
                <Textarea className="mt-2" rows={2} value={f.content || ""} onChange={(e) => updField(i, { content: e.target.value })} data-testid={`field-content-${i}`} placeholder="Content (HTML allowed for rich text)" />
              )}
              {HAS_OPTIONS.has(f.type) && (
                <div className="mt-2 space-y-1.5">
                  {(f.options || []).map((o, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <Input value={o} onChange={(e) => { const opts = [...f.options]; opts[oi] = e.target.value; updField(i, { options: opts }); }} data-testid={`field-option-${i}-${oi}`} />
                      <button onClick={() => updField(i, { options: f.options.filter((_, x) => x !== oi) })} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => updField(i, { options: [...(f.options || []), `Option ${(f.options || []).length + 1}`] })} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Option</Button>
                </div>
              )}
              {!DISPLAY.has(f.type) && (
                <label className="mt-2 flex items-center gap-2 text-xs text-slate-500"><Switch checked={!!f.required} onCheckedChange={(v) => updField(i, { required: v })} data-testid={`field-required-${i}`} /> Required</label>
              )}
            </div>
          ))}
          {form.fields.length === 0 && <p className="text-sm text-slate-400">Add fields using the dropdown above.</p>}
        </div>
      </Card>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader><DialogTitle>{form.title || "Preview"}</DialogTitle><DialogDescription>{form.description}</DialogDescription></DialogHeader>
          <div className="space-y-5">{form.fields.map((f) => <FormField key={f.id} field={f} value={f.type === "checkboxes" ? [] : ""} onChange={() => {}} readOnly={false} formId={id || "preview"} />)}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Submissions review ----------------
function ReviewDetail({ sid, onBack }) {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [comment, setComment] = useState("");
  const load = () => api.get(`/admin/submissions/${sid}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [sid]);

  const act = async (action) => {
    try { await api.patch(`/admin/submissions/${sid}/review`, { action, comment }); toast({ title: `Submission ${action}d` }); onBack(); }
    catch (e) { toast({ title: "Error", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };
  const download = async (path) => { try { const r = await api.get(`/forms/attachment/download`, { params: { path } }); window.open(r.data.url, "_blank", "noopener"); } catch (e) { toast({ title: "Download failed", variant: "destructive" }); } };

  if (!data) return <div className="py-10 text-center text-sm text-slate-400">Loading…</div>;
  const { submission: s, form } = data;

  return (
    <div className="space-y-5" data-testid="review-detail">
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-violet-600"><ArrowLeft className="h-4 w-4" /> Back to submissions</button>
      <Card>
        <h2 className="text-base font-bold text-slate-900">{form.title}</h2>
        <div className="mt-1 flex items-center gap-2"><StatusBadge status={s.status === "approved" ? "Approved" : s.status === "rejected" ? "Rejected" : "Pending"} /><span className="text-xs text-slate-400">{s.status}</span></div>
      </Card>
      <Card>
        <div className="space-y-5">
          {form.fields.map((f) => (
            <div key={f.id}>
              <FormField field={f} value={s.answers[f.id]} fileMeta={s.files[f.id]} onChange={() => {}} onFile={() => {}} readOnly formId={form.id} />
              {f.type === "file" && s.files[f.id]?.path && (
                <Button variant="outline" size="sm" onClick={() => download(s.files[f.id].path)} className="mt-1 gap-1.5" data-testid={`download-${f.id}`}><Download className="h-3.5 w-3.5" /> {s.files[f.id].name}</Button>
              )}
            </div>
          ))}
        </div>
      </Card>
      {s.signature && (
        <Card><h3 className="mb-2 text-sm font-bold text-slate-900">Electronic signature</h3>
          <div className="font-signature text-2xl text-slate-800">{s.signature.signature || s.signature.legal_name}</div>
          <div className="mt-1 text-xs text-slate-500">Legal name: {s.signature.legal_name} · Signed {s.signature.signed_at ? new Date(s.signature.signed_at).toLocaleString() : "—"} · User {s.signature.user_id?.slice(0, 8)} · IP {s.signature.ip || "n/a"}</div>
        </Card>
      )}
      {s.history?.length > 0 && (
        <Card><h3 className="mb-2 text-sm font-bold text-slate-900">History</h3>
          <div className="space-y-1.5">{s.history.map((h, i) => <div key={i} className="flex items-center gap-2 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" /> <strong>{h.action}</strong> by {h.by} · {new Date(h.at).toLocaleString()}{h.comment ? ` — "${h.comment}"` : ""}</div>)}</div>
        </Card>
      )}
      <Card>
        <Label>Reviewer comment</Label>
        <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" className="mt-1.5" placeholder="Optional note to the applicant" />
        <div className="mt-3 flex gap-2">
          <Button onClick={() => act("approve")} data-testid="approve-submission" className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"><Check className="h-4 w-4" /> Approve</Button>
          <Button onClick={() => act("return")} data-testid="return-submission" variant="outline" className="gap-1.5"><RotateCcw className="h-4 w-4" /> Return for corrections</Button>
          <Button onClick={() => act("reject")} data-testid="reject-submission" variant="outline" className="gap-1.5 border-rose-200 text-rose-600 hover:bg-rose-50"><X className="h-4 w-4" /> Reject</Button>
        </div>
      </Card>
    </div>
  );
}

function SubmissionsTab() {
  const [rows, setRows] = useState([]);
  const [openSid, setOpenSid] = useState(null);
  const load = () => api.get("/admin/submissions").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { if (!openSid) load(); }, [openSid]);
  if (openSid) return <ReviewDetail sid={openSid} onBack={() => setOpenSid(null)} />;
  const B = { submitted: "Pending", approved: "Approved", rejected: "Rejected", returned: "Rejected", in_progress: "In progress" };
  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400"><th className="py-2 pr-4">Applicant</th><th className="py-2 pr-4">Form</th><th className="py-2 pr-4">Status</th><th className="py-2 pr-4">Updated</th><th className="py-2 text-right">Action</th></tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-slate-50" data-testid={`submission-row-${s.id}`}>
                <td className="py-3 pr-4"><div className="font-medium text-slate-800">{s.applicant_name}</div><div className="text-xs text-slate-400">{s.applicant_email}</div></td>
                <td className="py-3 pr-4 text-slate-600">{s.form_title}</td>
                <td className="py-3 pr-4"><StatusBadge status={B[s.status] || "Pending"} /></td>
                <td className="py-3 pr-4 text-slate-500">{s.updated_at ? new Date(s.updated_at).toLocaleDateString() : "—"}</td>
                <td className="py-3 text-right"><Button size="sm" variant="outline" onClick={() => setOpenSid(s.id)} data-testid={`review-${s.id}`}>Review</Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-slate-400">No submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/forms/analytics").then((r) => setData(r.data)).catch(() => setData({ applicants: 0, forms: [] })); }, []);
  if (!data) return <div className="py-10 text-center text-sm text-slate-400">Loading…</div>;
  const Bar = ({ pct, color }) => (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
  );
  return (
    <Card data-testid="forms-analytics">
      <div className="mb-3">
        <h3 className="text-sm font-bold text-slate-900">Form analytics</h3>
        <p className="text-sm text-slate-500">Completion and approval rates across {data.applicants} applicant{data.applicants === 1 ? "" : "s"} — spot where candidates drop off.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
            <th className="py-2 pr-4">Form</th><th className="py-2 pr-3 text-center">Started</th><th className="py-2 pr-3 text-center">In&nbsp;prog</th><th className="py-2 pr-3 text-center">Submitted</th><th className="py-2 pr-3 text-center">Approved</th><th className="py-2 pr-3 text-center">Rejected</th><th className="py-2 pr-3 text-center">Returned</th><th className="py-2 pr-4">Completion</th><th className="py-2">Approval</th>
          </tr></thead>
          <tbody>
            {data.forms.map((f) => (
              <tr key={f.id} className="border-b border-slate-50" data-testid={`analytics-row-${f.id}`}>
                <td className="py-3 pr-4"><div className="font-medium text-slate-800">{f.title}</div><div className="text-xs text-slate-400">{f.published ? "Published" : "Draft"}{f.required ? " · Required" : " · Optional"}</div></td>
                <td className="py-3 pr-3 text-center text-slate-600">{f.started}</td>
                <td className="py-3 pr-3 text-center text-slate-600">{f.counts.in_progress}</td>
                <td className="py-3 pr-3 text-center text-slate-600">{f.counts.submitted}</td>
                <td className="py-3 pr-3 text-center text-emerald-600">{f.counts.approved}</td>
                <td className="py-3 pr-3 text-center text-rose-600">{f.counts.rejected}</td>
                <td className="py-3 pr-3 text-center text-amber-600">{f.counts.returned}</td>
                <td className="py-3 pr-4"><div className="flex items-center gap-2"><Bar pct={f.completion_rate} color="bg-violet-500" /><span className="w-9 text-right text-xs font-semibold text-slate-600">{f.completion_rate}%</span></div></td>
                <td className="py-3"><div className="flex items-center gap-2"><Bar pct={f.approval_rate} color="bg-emerald-500" /><span className="w-9 text-right text-xs font-semibold text-slate-600">{f.approval_rate}%</span></div></td>
              </tr>
            ))}
            {data.forms.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400">No forms yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------------- Main ----------------
export default function FormsManager() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/forms").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { if (!editing) load(); }, [editing]);

  const del = async (id) => { if (!window.confirm("Delete this form?")) return; await api.delete(`/admin/forms/${id}`); toast({ title: "Form deleted" }); load(); };
  const dup = async (id) => { await api.post(`/admin/forms/${id}/duplicate`); toast({ title: "Form duplicated" }); load(); };
  const togglePub = async (f) => { await api.put(`/admin/forms/${f.id}`, { ...f, published: !f.published, sort_order: f.order }); toast({ title: f.published ? "Unpublished" : "Published" }); load(); };
  const move = async (i, d) => { const n = [...rows]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; setRows(n); await api.post("/admin/forms/reorder", { ids: n.map((x) => x.id) }); load(); };

  if (editing) return <FormBuilder formId={editing} onBack={() => setEditing(null)} />;

  return (
    <div className="space-y-4" data-testid="forms-manager">
      <Tabs defaultValue="forms">
        <TabsList><TabsTrigger value="forms" data-testid="tab-forms">Forms</TabsTrigger><TabsTrigger value="submissions" data-testid="tab-submissions">Submissions</TabsTrigger><TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger></TabsList>
        <TabsContent value="forms" className="mt-4 space-y-3">
          <Card>
            <div className="flex items-center justify-between">
              <div><h3 className="text-sm font-bold text-slate-900">Forms Manager</h3><p className="text-sm text-slate-500">Build and manage onboarding forms — no code required.</p></div>
              <Button onClick={() => setEditing("new")} data-testid="new-form-btn" className="btn-primary-gradient gap-1.5 border-0 text-white"><Plus className="h-4 w-4" /> New form</Button>
            </div>
          </Card>
          {rows.map((f, i) => (
            <Card key={f.id}>
              <div className="flex items-center justify-between gap-3" data-testid={`form-row-${f.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex flex-col"><button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-violet-600 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button><button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="text-slate-400 hover:text-violet-600 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button></div>
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-100 text-violet-600"><ClipboardList className="h-4 w-4" /></div>
                  <div><div className="font-medium text-slate-900">{i + 1}. {f.title}</div><div className="text-sm text-slate-500">{f.fields.length} fields · {f.required ? "Required" : "Optional"} · stage: {f.stage}</div></div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${f.published ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-500"}`}>{f.published ? "Published" : "Draft"}</span>
                  <label className="flex items-center gap-2 text-xs text-slate-500"><Switch checked={!!f.published} onCheckedChange={() => togglePub(f)} data-testid={`pub-toggle-${f.id}`} /> Publish</label>
                  <button onClick={() => setEditing(f.id)} className="rounded-md p-1.5 text-slate-400 hover:bg-violet-50 hover:text-violet-600" data-testid={`edit-form-${f.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => dup(f.id)} className="rounded-md p-1.5 text-slate-400 hover:bg-violet-50 hover:text-violet-600" data-testid={`dup-form-${f.id}`}><Copy className="h-4 w-4" /></button>
                  <button onClick={() => del(f.id)} className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`del-form-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </Card>
          ))}
          {rows.length === 0 && <Card><p className="py-6 text-center text-sm text-slate-400">No forms yet. Click "New form".</p></Card>}
        </TabsContent>
        <TabsContent value="submissions" className="mt-4"><SubmissionsTab /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><AnalyticsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

export function FormReviews() {
  return (
    <div className="space-y-4" data-testid="form-reviews">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6">
        <h3 className="text-sm font-bold text-slate-900">Form Reviews</h3>
        <p className="text-sm text-slate-500">Review, approve, return, or reject form submissions from your assigned candidates.</p>
      </div>
      <SubmissionsTab />
    </div>
  );
}
